import { useEffect, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { supabase } from "../services/supabase";
import { getErrorMessage } from "@utils/helper";

/**
 * Pulls the current student's profile fields from the real schema.
 *
 * The StudentProfile page used to read everything from `user.details.*`
 * (e.g. `user.details.id_number`, `user.details.student_grade_level
 * .curriculum.curriculum_name`). But the `users` table has no `details`
 * column — student-specific fields live on the `students` table, and the
 * grade-level / curriculum / school-year linkage lives in
 * `student_grade_levels`. As a result, every profile field rendered
 * blank or "-" in production. Surfaced 2026-05-16 in bug 3d623af7.
 *
 * This hook does the proper join in one place so the UI can stay simple.
 */
export interface StudentProfileDetails {
  id_number: string | null;
  dob: string | null;
  user_name: string | null;
  contact_number: string | null;
  grade_level: string | null;
  curriculum_name: string | null;
  school_year_name: string | null;
  class_name: string | null;
  // "Program" is NOT a derived/joined value — it's a curriculum custom field.
  // Profile screens read it (and any other tenant-defined attribute) via
  // useStudentCurriculumCustomFields(), not from here.
  // No per-student "expiring on" column exists in the schema yet; surfaced as
  // null so the profile renders the labelled row with a "—" placeholder.
  expiring_on: string | null;
}

export function useStudentProfileDetails(): {
  details: StudentProfileDetails | null;
  loading: boolean;
  error: string | null;
} {
  const { profile } = useAuth();
  const studentId = profile?.student_id ?? null;
  const [details, setDetails] = useState<StudentProfileDetails | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!studentId) {
      setLoading(true);
      setDetails(null);
      return;
    }
    setLoading(true);
    setError(null);

    (async () => {
      try {
        // 1. Core student fields.
        const { data: student, error: sErr } = await supabase
          .from("students")
          .select("id_number, dob, user_name, contact_number")
          .eq("id", studentId)
          .maybeSingle();
        if (sErr) throw sErr;

        // 2. Active grade/year/curriculum linkage. There may be more than
        // one row historically; pick the active one (or most recently
        // updated as fallback).
        // Curriculum lives on `school_years.curriculum_id` (not on
        // `grade_levels` — that table has no curriculum_id). Embedding
        // `grade_levels(curriculum_id, curriculums(...))` produced a
        // PostgREST 400 that blanked every field. Reach curriculum through
        // the school year instead, where the FK actually exists.
        const { data: sgl, error: gErr } = await supabase
          .from("student_grade_levels")
          .select(
            "active, updated_at, grade_levels(grade_level), school_years(name, curriculums(curriculum_name))",
          )
          .eq("student_id", studentId)
          .order("active", { ascending: false })
          .order("updated_at", { ascending: false })
          .limit(1);
        if (gErr) throw gErr;
        const top = (sgl ?? [])[0];
        // to-one embeds may be typed as arrays by PostgREST — normalize each.
        const first = <V>(v: V | V[] | null | undefined): V | undefined =>
          Array.isArray(v) ? v[0] : (v ?? undefined);
        const gradeLevel = first(top?.grade_levels);
        const schoolYear = first(top?.school_years);
        const curriculum = first(schoolYear?.curriculums);

        // 3. Class name via the class_students junction. (Program is a
        // curriculum custom field — see useStudentCurriculumCustomFields —
        // not a class→course join, so we only need the class name here. The
        // previous `class_courses(courses(...))` embed referenced a junction
        // that doesn't exist and 400'd, which also blanked class_name.)
        const { data: classRows } = await supabase
          .from("class_students")
          .select("classes(class_name)")
          .eq("student_id", studentId);
        const firstClass = first((classRows ?? [])[0]?.classes);
        const className = firstClass?.class_name ?? null;

        if (cancelled) return;
        setDetails({
          id_number: student?.id_number ?? null,
          dob: student?.dob ?? null,
          user_name: student?.user_name ?? null,
          contact_number: student?.contact_number ?? null,
          grade_level: gradeLevel?.grade_level ?? null,
          curriculum_name: curriculum?.curriculum_name ?? null,
          school_year_name: schoolYear?.name ?? null,
          class_name: className,
          expiring_on: null,
        });
      } catch (err: unknown) {
        if (cancelled) return;
        setError(getErrorMessage(err, 'Failed to load profile details'));
        setDetails(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [studentId]);

  return { details, loading, error };
}
